import { useTranslation } from 'react-i18next'
import { Tooltip } from './Tooltip'

interface StoreBadgesProps {
  className?: string
  variant?: 'light' | 'dark'
}

type Lang = 'de' | 'en'

const APPLE_LABEL: Record<Lang, string> = {
  de: 'Laden im App Store',
  en: 'Download on the App Store',
}

const GOOGLE_LABEL: Record<Lang, string> = {
  de: 'Jetzt bei Google Play',
  en: 'Get it on Google Play',
}

export function StoreBadges({ className = '', variant = 'light' }: StoreBadgesProps) {
  const { t, i18n } = useTranslation()
  const lang: Lang = i18n.language?.toLowerCase().startsWith('de') ? 'de' : 'en'

  const appleUrl = t('storeLinks.appStore', { defaultValue: '' }).trim()
  const googleUrl = t('storeLinks.googlePlay', { defaultValue: '' }).trim()
  const appleEnabled = appleUrl.length > 0
  const googleEnabled = googleUrl.length > 0

  const tooltipText = t('nav.comingSoonTooltip')

  const appleSrc = `/badges/app-store-${lang}.svg`
  const googleSrc = `/badges/google-play-${lang}.svg`
  const appleAlt = APPLE_LABEL[lang]
  const googleAlt = GOOGLE_LABEL[lang]

  const enabledClass = 'transition-transform duration-200 hover:scale-[1.03] active:scale-[0.98]'
  const disabledClass = 'opacity-60 grayscale cursor-not-allowed'

  return (
    <div className={`flex flex-wrap items-center gap-3 sm:gap-4 ${className}`}>
      {appleEnabled ? (
        <a
          href={appleUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={appleAlt}
          className={`inline-block ${enabledClass}`}
        >
          <img src={appleSrc} alt={appleAlt} className="block h-[54px] w-auto" loading="lazy" />
        </a>
      ) : (
        <Tooltip content={tooltipText} position="bottom" className="inline-block">
          <span aria-label={appleAlt} className={`inline-block ${disabledClass}`}>
            <img src={appleSrc} alt={appleAlt} className="block h-[54px] w-auto pointer-events-none" loading="lazy" />
          </span>
        </Tooltip>
      )}

      {googleEnabled ? (
        <a
          href={googleUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={googleAlt}
          className={`inline-block ${enabledClass}`}
        >
          <img src={googleSrc} alt={googleAlt} className="block h-[54px] w-auto" loading="lazy" />
        </a>
      ) : (
        <Tooltip content={tooltipText} position="bottom" className="inline-block">
          <span aria-label={googleAlt} className={`inline-block ${disabledClass}`}>
            <img src={googleSrc} alt={googleAlt} className="block h-[54px] w-auto pointer-events-none" loading="lazy" />
          </span>
        </Tooltip>
      )}

      {/* `variant` ist als API-Parameter angelegt, falls spaeter helle Badges (z.B. ueber dunklen Hintergruenden) gewuenscht sind. Aktuell nur ein Style. */}
      <span className="sr-only" data-variant={variant} />
    </div>
  )
}
